// Team & access — merchant-membership management, wired to
// /v1/merchants/:id/memberships (Phase 06.1).
//
// The screen manages the team of the *current* merchant (X-Merchant-ID).
// payouts-backend has no user table — identity is delegated to auth-service —
// so a member is (auth-service user_id, role). The "Roles" tab is a static
// reference for the real two-tier RBAC model, not backend data.
import { useEffect, useRef, useState } from "react";

import { I } from "@/components/Icons";
import { Modal, StatusPill } from "@/components/UI";
import { formatDateTime } from "@/utils/format";
import { useMemberships } from "@/queries/useMemberships";
import { useMerchants } from "@/queries/useMerchants";
import { useAuth, isAdminRole } from "@/store/auth";
import { useUI } from "@/store/ui";
import { useQueryClient } from "@tanstack/react-query";
import {
  createMembership,
  updateMembership,
  deleteMembership,
  type Membership,
  type MembershipRole,
} from "@/api/memberships";
import {
  resolveUserByEmail,
  inviteUser,
  UserNotFoundError,
} from "@/api/users";

const ROLES: MembershipRole[] = ["owner", "operator", "viewer"];

// Membership-specific error copy lives in @/utils/apiError. Keep this file
// free of duplicated mappings so every page surfaces the same copy for the
// same backend code. Re-export the shared helper under the local name so
// existing call sites read unchanged.
import { apiErrorMessage as errorMessage } from "@/utils/apiError";

export function Rbac() {
  const [tab, setTab] = useState<"team" | "roles">("team");

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console · Access</div>
          <h1>Team &amp; access</h1>
          <div className="sub">
            Members and roles for the selected merchant
          </div>
        </div>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={"tab" + (tab === "team" ? " active" : "")}
          onClick={() => setTab("team")}
        >
          Team
        </button>
        <button
          type="button"
          className={"tab" + (tab === "roles" ? " active" : "")}
          onClick={() => setTab("roles")}
        >
          Roles
        </button>
      </div>

      {tab === "team" ? <TeamTab /> : <RolesTab />}
    </div>
  );
}

// ─── Team tab ─────────────────────────────────────────────────────────────

function TeamTab() {
  const qc = useQueryClient();
  const merchantId = useUI((s) => s.currentMerchantId);
  const user = useAuth((s) => s.user);

  const { data: members = [], isLoading, isError } = useMemberships();

  // canManage: global admin, or owner of the current merchant. The server
  // enforces this regardless — this only gates UI affordances.
  const myRole = user?.memberships.find(
    (m) => m.merchant_id === merchantId,
  )?.role;
  const canManage = isAdminRole(user?.role) || myRole === "owner";

  const [showAdd, setShowAdd] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Membership | null>(null);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  if (merchantId == null) {
    return (
      <div className="card">
        <div className="empty">
          <div className="icon">
            <I.Users size={16} />
          </div>
          <h3>No merchant selected</h3>
          <p>
            Team management is per-merchant. Select a merchant to view and
            manage its members.
          </p>
        </div>
      </div>
    );
  }

  async function changeRole(m: Membership, role: MembershipRole) {
    if (merchantId == null || role === m.role) return;
    setRowError(null);
    setBusyUserId(m.user_id);
    try {
      await updateMembership(merchantId, m.user_id, role);
      void qc.invalidateQueries({ queryKey: ["memberships", merchantId] });
    } catch (err) {
      setRowError(errorMessage(err, "Couldn't change role"));
    } finally {
      setBusyUserId(null);
    }
  }

  return (
    <>
      <div className="row" style={{ justifyContent: "flex-end", marginBottom: 12 }}>
        {/* Ungated — server enforces. Hiding it on canManage=false confuses
            admins whose role detection has a hiccup (the modal POST is the
            authoritative check and surfaces 403 cleanly if unauthorized). */}
        <button
          className="btn accent"
          type="button"
          onClick={() => setShowAdd(true)}
        >
          <I.Plus size={12} /> Add member
        </button>
      </div>

      {rowError && (
        <div style={{ color: "var(--neg)", fontSize: 12, marginBottom: 12 }}>
          {rowError}
        </div>
      )}

      <div className="card">
        {isLoading ? (
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>
              Loading team…
            </div>
          </div>
        ) : isError ? (
          <div className="empty">
            <h3>Couldn't load the team</h3>
            <p>The request to payouts-backend failed. Try again shortly.</p>
          </div>
        ) : members.length === 0 ? (
          <div className="empty">
            <div className="icon">
              <I.Users size={16} />
            </div>
            <h3>No members yet</h3>
            <p>Members of this merchant team will appear here.</p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Added</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.user_id}>
                  <td>
                    {/* Display priority: display_name (migration 024,
                        captured at invite) → email (auth-service
                        enrichment) → "User #N" (pre-024 fallback when
                        neither is available). Sub-line carries the
                        secondary identifier so the operator always
                        knows the email behind the name. */}
                    <div className="cell-name">
                      {m.display_name && m.display_name !== ""
                        ? m.display_name
                        : m.email && m.email !== ""
                          ? m.email
                          : `User #${m.user_id}`}
                    </div>
                    <div className="cell-id">
                      {m.display_name && m.display_name !== "" && m.email
                        ? m.email
                        : m.email && m.email !== ""
                          ? `user #${m.user_id}`
                          : "auth-service user"}
                    </div>
                  </td>
                  <td>
                    {canManage ? (
                      <select
                        className="input"
                        style={{ width: 130 }}
                        value={m.role}
                        disabled={busyUserId === m.user_id}
                        onChange={(e) =>
                          changeRole(m, e.target.value as MembershipRole)
                        }
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <StatusPill status={m.role} />
                    )}
                  </td>
                  <td className="dim">{formatDateTime(m.created_at)}</td>
                  {canManage && (
                    <td className="right">
                      <button
                        className="btn ghost sm danger"
                        type="button"
                        disabled={busyUserId === m.user_id}
                        onClick={() => setRemoveTarget(m)}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/*
        AUDIT-PASS-6 critical-tech-lead Finding #2: key={merchantId}
        forces unmount+remount when the topbar MerchantSwitcher flips
        the active merchant while this modal is open. Without it the
        modal kept its email/lookup/role state across the change —
        an operator who'd typed an email, gotten a "Found" result for
        merchant A, then switched to merchant B in the topbar, would
        click "Add member" and attach the merchant-A-resolved user to
        merchant B's team. Tiny chance, irreversible outcome.

        merchantId is `number` here (narrowed by the early-return at
        line 96). No null fallback needed; an earlier "?? 'no-merchant'"
        was dead code that misled future readers about the invariant —
        if anyone removes the early-return, this whole block needs
        a real null branch, not a string-key fallback.
      */}
      <AddMemberModal
        key={merchantId}
        open={showAdd}
        merchantId={merchantId}
        onClose={() => setShowAdd(false)}
      />
      {canManage && (
        <RemoveMemberModal
          merchantId={merchantId}
          member={removeTarget}
          onClose={() => setRemoveTarget(null)}
        />
      )}
    </>
  );
}

// ─── Add-member modal ─────────────────────────────────────────────────────
//
// AUDIT-PASS-6 follow-up. Previously asked the operator for the target's
// numeric auth-service user_id — a five-step pain (open auth-service admin
// UI, search by email, copy the ID, paste it here, hope you got the right
// human). Now the operator just types an email and the modal handles both
// branches:
//
//   - User already exists in auth-service       → "Add" (createMembership)
//   - User doesn't exist in auth-service yet    → "Invite & add" (inviteUser)
//
// The lookup is debounced (400ms) so we don't fire a request on every
// keystroke. A monotonically-incrementing token guards against races:
// when the email changes mid-flight, the in-flight result is dropped
// (otherwise a slow lookup for "jane@acme.in" could land after a faster
// lookup for "j@x.io" and overwrite its result).
//
// Storage model is unchanged: merchant_memberships is still keyed by
// (user_id, merchant_id). The inviteUser path resolves the email to a
// user_id server-side and creates the membership in one atomic backend
// call (see /v1/merchants/:id/users/invite).

type LookupState =
  | { kind: "idle" }
  | { kind: "looking" }
  | { kind: "found"; user_id: number; email: string }
  | { kind: "not_found"; email: string }
  | { kind: "error"; message: string };

const LOOKUP_DEBOUNCE_MS = 400;

// Light shape-check that matches the backend's looksLikeEmail —
// "anything@anything.anything". We don't enforce strict RFC 5322;
// the backend (which forwards to auth-service) is the canonical
// validator. Doing the shape check client-side just avoids firing
// a lookup for input that's obviously not an email yet.
function isPlausibleEmail(s: string): boolean {
  const at = s.indexOf("@");
  if (at <= 0 || at === s.length - 1) return false;
  return s.indexOf(".", at + 1) > at + 1;
}

function AddMemberModal({
  open,
  merchantId,
  onClose,
}: {
  open: boolean;
  merchantId: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { addToast } = useUI();
  // AUDIT-PASS-9 follow-up: the modal previously inherited merchantId
  // implicitly from the topbar switcher with no visible indicator of which
  // merchant the role attaches to ("operator OF WHICH merchant?"). Now:
  //   - Admins see a selector for ANY merchant they can manage.
  //   - Non-admins see a static label of the current merchant (they can
  //     only manage one merchant at a time anyway).
  // The modal is keyed by merchantId at its call site, so the initial
  // state is the prop value; an admin who changes the selector inside
  // the modal updates only the local state (no topbar mutation).
  const authUser = useAuth((s) => s.user);
  const isAdmin = isAdminRole(authUser?.role);
  const { data: allMerchants = [] } = useMerchants();
  const [targetMerchantId, setTargetMerchantId] = useState(merchantId);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<MembershipRole>("viewer");
  const [lookup, setLookup] = useState<LookupState>({ kind: "idle" });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Resolve the human-readable merchant label for the current target.
  // Admins use the full /v1/merchants list (display_name available);
  // non-admins fall back to "Merchant #X · role" from their memberships
  // (no display_name exposed at the /v1/me boundary).
  let targetMerchantLabel = `Merchant #${targetMerchantId}`;
  if (isAdmin) {
    const m = allMerchants.find((x) => x.id === targetMerchantId);
    if (m) targetMerchantLabel = `${m.display_name} (#${m.id})`;
  } else if (authUser?.memberships) {
    const ms = authUser.memberships.find((x) => x.merchant_id === targetMerchantId);
    if (ms) {
      // Prefer display_name from /v1/me's joined-with-merchants response.
      // Pre-fix backends omit it; fall back to the bare "Merchant #N"
      // label so the modal stays usable on a mid-rolling-deploy panel
      // hitting an older /v1/me.
      targetMerchantLabel = ms.display_name
        ? `${ms.display_name} (#${ms.merchant_id}) · you are ${ms.role}`
        : `Merchant #${ms.merchant_id} · you are ${ms.role}`;
    }
  }

  // Race guard. Every new lookup bumps this; effects compare against
  // their captured value and skip applying a result if the user has
  // moved on (typing a different email, or closing the modal).
  const lookupToken = useRef(0);

  function reset() {
    setEmail("");
    setDisplayName("");
    setRole("viewer");
    setLookup({ kind: "idle" });
    setSubmitting(false);
    setSubmitError(null);
    lookupToken.current++;
    onClose();
  }

  // Debounced lookup. Re-runs whenever the trimmed email changes.
  useEffect(() => {
    // AUDIT-PASS-6 critical-tech-lead Finding #1: bump the token at the
    // TOP of every effect run, before any early-return. The earlier
    // version only bumped on the dispatched-lookup path — so a clear-
    // field-after-fire scenario (type "jane@acme.in" → fire at 400ms
    // → clear at 500ms → resolve at 900ms) saw the in-flight lookup's
    // captured token still equal to lookupToken.current, applied its
    // result, and left the modal in a phantom `kind:"found"` state
    // with Submit enabled while the input was empty. Clicking Submit
    // would attach the resolved user to a membership with an empty
    // visual context. Bumping unconditionally invalidates every prior
    // in-flight lookup whenever the effect re-runs for ANY reason.
    lookupToken.current++;
    const myToken = lookupToken.current;

    const trimmed = email.trim();
    setSubmitError(null);
    if (!open) return;
    if (trimmed === "") {
      setLookup({ kind: "idle" });
      return;
    }
    if (!isPlausibleEmail(trimmed)) {
      // Keep the state at "idle" while the user is typing — flashing
      // a validation error mid-keystroke is jarring.
      setLookup({ kind: "idle" });
      return;
    }

    setLookup({ kind: "looking" });
    const handle = window.setTimeout(() => {
      void resolveUserByEmail(targetMerchantId, trimmed)
        .then((resolved) => {
          if (myToken !== lookupToken.current) return;
          setLookup({
            kind: "found",
            user_id: resolved.user_id,
            email: resolved.email,
          });
        })
        .catch((err) => {
          if (myToken !== lookupToken.current) return;
          if (err instanceof UserNotFoundError) {
            setLookup({ kind: "not_found", email: trimmed });
            return;
          }
          setLookup({
            kind: "error",
            message: errorMessage(err, "Lookup failed. Try again."),
          });
        });
    }, LOOKUP_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [email, targetMerchantId, open]);

  // Submit handler branches on the resolved lookup state. The button is
  // only enabled when lookup is in `found` or `not_found`, so a malformed
  // submission (e.g. via Enter while still looking) is just a no-op.
  //
  // The handler takes no event — both call sites (the form's onSubmit and
  // the button's onClick) pre-handle their own event (preventDefault for
  // the form, nothing for the button). This avoids the cross-form-submit
  // brittleness where `<button form="add-member-form">` outside the form
  // sometimes failed to trigger React's onSubmit and let the browser
  // execute the form's default action (a GET on the current URL — i.e.,
  // a page refresh that ate the operator's click). Other modals in the
  // codebase use the same pattern; if any of them surface the same
  // "refresh but nothing happens" symptom, port this fix there too.
  async function handleSubmit() {
    if (lookup.kind !== "found" && lookup.kind !== "not_found") return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const trimmedName = displayName.trim();
      if (lookup.kind === "found") {
        await createMembership(targetMerchantId, {
          user_id: lookup.user_id,
          role,
          display_name: trimmedName !== "" ? trimmedName : undefined,
        });
        addToast({
          title: "Member added",
          body: `${trimmedName !== "" ? trimmedName + " · " : ""}${lookup.email} · ${role} of ${targetMerchantLabel}`,
          variant: "success",
        });
      } else {
        const result = await inviteUser(targetMerchantId, {
          email: lookup.email,
          role,
          display_name: trimmedName !== "" ? trimmedName : undefined,
        });
        addToast({
          title: result.already_member ? "Already on the team" : "Invite sent",
          body: result.already_member
            ? `${result.email} · ${result.membership.role} of ${targetMerchantLabel}`
            : `${result.email} · ${role} of ${targetMerchantLabel}. They'll join after their first OTP login.`,
          variant: "success",
        });
      }
      void qc.invalidateQueries({ queryKey: ["memberships", targetMerchantId] });
      reset();
    } catch (err) {
      setSubmitError(errorMessage(err, "Couldn't add member"));
      setSubmitting(false);
    }
  }

  const canSubmit =
    !submitting && (lookup.kind === "found" || lookup.kind === "not_found");

  const primaryLabel =
    lookup.kind === "not_found"
      ? submitting
        ? "Inviting…"
        : "Invite & add"
      : submitting
        ? "Adding…"
        : "Add member";

  return (
    <Modal
      open={open}
      onClose={reset}
      busy={submitting}
      title="Add team member"
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={reset}
            disabled={submitting}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {primaryLabel}
          </button>
        </>
      }
    >
      <form
        id="add-member-form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        className="stack-16"
      >
        {/* AUDIT-PASS-9 follow-up: explicit target-merchant context. */}
        <div className="field">
          <label className="label" htmlFor="add-member-merchant">
            Merchant
          </label>
          {isAdmin && allMerchants.length > 0 ? (
            <select
              id="add-member-merchant"
              className="input"
              value={targetMerchantId}
              onChange={(e) => setTargetMerchantId(Number(e.target.value))}
            >
              {allMerchants.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.display_name} (#{m.id})
                </option>
              ))}
            </select>
          ) : (
            <div
              id="add-member-merchant"
              className="input"
              style={{ color: "var(--ink-3)", userSelect: "none" }}
              aria-readonly="true"
            >
              {targetMerchantLabel}
            </div>
          )}
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            The role below applies to this merchant only. A user can have a
            different role on each merchant.
          </div>
        </div>

        {/* PDF review 26 May 26 (round 2): collect the team member's full
            name at invite time so the team listing renders a human label
            instead of 'User #N'. Stored on merchant_memberships
            (migration 024) per-merchant — a user can carry different
            display_names across merchants without auth-service caring. */}
        <div className="field">
          <label className="label" htmlFor="add-member-name">
            Full name
          </label>
          <input
            id="add-member-name"
            className="input"
            type="text"
            placeholder="Jane Doe"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="off"
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            How this member is shown in the team list. Email is still the
            identifier; this is just for display.
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="add-member-email">
            Email
          </label>
          <input
            id="add-member-email"
            className="input"
            type="email"
            placeholder="jane@acme.in"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            autoFocus
            aria-describedby="add-member-status"
          />
          <div
            id="add-member-status"
            className="dim"
            style={{ fontSize: 11, marginTop: 4, minHeight: 16 }}
            aria-live="polite"
          >
            {renderLookupStatus(lookup)}
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="add-member-role">
            Role
          </label>
          <select
            id="add-member-role"
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as MembershipRole)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        {submitError && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{submitError}</div>
        )}
      </form>
    </Modal>
  );
}

// renderLookupStatus formats the lookup state into a single short line of
// inline copy. Lives outside the component so the conditional can be read
// in one place rather than weaving through JSX. Returns "" for idle so
// the reserved 16px slot collapses visually but stays in the layout.
function renderLookupStatus(lookup: LookupState): string {
  switch (lookup.kind) {
    case "idle":
      return "";
    case "looking":
      return "Looking up…";
    case "found":
      return `Found: ${lookup.email} (user #${lookup.user_id})`;
    case "not_found":
      return `${lookup.email} isn't a payouts user yet — we'll invite them.`;
    case "error":
      return lookup.message;
  }
}

// ─── Remove-member modal ──────────────────────────────────────────────────

function RemoveMemberModal({
  merchantId,
  member,
  onClose,
}: {
  merchantId: number;
  member: Membership | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setSubmitting(false);
    setError(null);
    onClose();
  }

  async function handleConfirm() {
    if (!member) return;
    setError(null);
    setSubmitting(true);
    try {
      await deleteMembership(merchantId, member.user_id);
      void qc.invalidateQueries({ queryKey: ["memberships", merchantId] });
      reset();
    } catch (err) {
      setError(errorMessage(err, "Couldn't remove member"));
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={member != null}
      onClose={reset}
      busy={submitting}
      title="Remove team member"
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={reset}
            disabled={submitting}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn danger"
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? "Removing…" : "Remove member"}
          </button>
        </>
      }
    >
      <div className="stack-16">
        {member && (
          <div
            style={{
              background: "var(--bg-sunk)",
              padding: "10px 12px",
              borderRadius: 3,
              fontSize: 12,
            }}
          >
            <span className="dim">Member: </span>
            <strong>
              {member.display_name && member.display_name !== ""
                ? member.display_name
                : member.email && member.email !== ""
                  ? member.email
                  : `User #${member.user_id}`}
            </strong>
            <span className="dim" style={{ marginLeft: 8 }}>
              {member.role}
            </span>
          </div>
        )}
        <div className="dim" style={{ fontSize: 12 }}>
          This revokes the user's access to this merchant. They can be added
          back later. The last owner cannot be removed.
        </div>
        {error && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{error}</div>
        )}
      </div>
    </Modal>
  );
}

// ─── Roles reference tab ──────────────────────────────────────────────────

const ROLE_REFERENCE: { role: string; summary: string }[] = [
  {
    role: "owner",
    summary:
      "Full control of the merchant: manage the team, bank rails, API keys, and submit payouts. A merchant must always keep at least one owner.",
  },
  {
    role: "operator",
    summary:
      "Day-to-day operations: submit single and batch payouts, mint and rotate API keys. Cannot manage the team or bank rails.",
  },
  {
    role: "viewer",
    summary:
      "Read-only: view payouts, batches, bank rails, API keys, and the team. Cannot move money or change anything.",
  },
];

function RolesTab() {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Per-merchant roles</h3>
        <div className="spacer" />
        <span className="dim" style={{ fontSize: 11 }}>
          enforced by payouts-backend
        </span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Role</th>
            <th>What it grants</th>
          </tr>
        </thead>
        <tbody>
          {ROLE_REFERENCE.map((r) => (
            <tr key={r.role}>
              <td>
                <StatusPill status={r.role} />
              </td>
              <td className="dim">{r.summary}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div
        className="card-body dim"
        style={{ fontSize: 11, borderTop: "1px solid var(--hair)" }}
      >
        Platform admins are defined globally and have no membership row — they
        can access every merchant regardless of the table above.
      </div>
    </div>
  );
}
